import { useState, useEffect } from "react";
import { motion } from "../../AuthNew/shared/motionUtils";
import { pushAdTrackingEvent } from "../../../utils/adTracking";
import { useLocation } from "react-router-dom";
import {
  LoadingContainer,
  LoadingIcon,
  LoadingText,
  LoadingSteps,
  LoadingStep,
  LoadingStepIcon,
  scaleIn,
} from "../shared/onboardingStyles";
import { Icon } from "../../../components/Icon";
import { ReactComponent as Timer } from "../../../assets/img/icons/timer.svg";
import { ReactComponent as TickCircle } from "../../../assets/img/icons/tick_circle.svg";

const Step5Loading = ({ onComplete, userInfo, selectedOrganization }) => {
  const location = useLocation();
  const [completedSteps, setCompletedSteps] = useState([]);

  const steps = [
    { id: 1, label: "Creating your workspace" },
    { id: 2, label: "Setting up everything" },
    { id: 3, label: "Preparing your dashboard" },
  ];

  useEffect(() => {
    // Simulate step completion
    const timer1 = setTimeout(() => {
      setCompletedSteps([1]);
    }, 1500);

    const timer2 = setTimeout(() => {
      setCompletedSteps([1, 2]);
    }, 3000);

    const timer3 = setTimeout(() => {
      setCompletedSteps([1, 2, 3]);
    }, 4500);

    const timer4 = setTimeout(() => {
      // Track onboarding completion
      pushAdTrackingEvent({
        event: "signup_onboarding_completed",
        location,
        selectedOrganization,
        user_id: userInfo?.user_id || null,
        full_name: userInfo?.first_name && userInfo?.last_name 
          ? `${userInfo.first_name} ${userInfo.last_name}` 
          : userInfo?.first_name || null,
        email: userInfo?.email || null,
      });
      
      onComplete();
    }, 4500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [onComplete]);

  return (
    <motion.div
      variants={scaleIn}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
    >
      <LoadingContainer>
        <LoadingIcon
          animate={{
            rotate: [0, 360],
            scale: [1, 1.05, 1],
          }}
          transition={{
            rotate: {
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            },
            scale: {
              duration: 1,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
        >
          <Icon component={Timer} size="40px" />
        </LoadingIcon>

        <LoadingText>Setting up your workspace...</LoadingText>

        <LoadingSteps>
          {steps.map((step, index) => {
            const isComplete = completedSteps.includes(step.id);
            return (
              <LoadingStep
                key={step.id}
                isComplete={isComplete}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2 }}
              >
                <LoadingStepIcon
                  isComplete={isComplete}
                  animate={
                    isComplete
                      ? {
                          scale: [1, 1.2, 1],
                        }
                      : {}
                  }
                  transition={{ duration: 0.3 }}
                >
                  {isComplete ? (
                    <Icon component={TickCircle} size="14px" />
                  ) : (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      style={{
                        width: "12px",
                        height: "12px",
                        border: "2px solid #9ca3af",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                      }}
                    />
                  )}
                </LoadingStepIcon>
                {step.label}
              </LoadingStep>
            );
          })}
        </LoadingSteps>
      </LoadingContainer>
    </motion.div>
  );
};

export default Step5Loading;
